import React, { Component, ErrorInfo, ReactNode } from 'react';

interface IErrorBoundaryProps {
  children: ReactNode;
  // Rendered instead of the children when they throw. Defaults to nothing,
  // which is the right choice for non-critical UI (e.g. lazy modals) - the
  // rest of the app keeps working.
  fallback?: ReactNode;
  // Optional hook for logging.
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface IErrorBoundaryState {
  hasError: boolean;
}

/**
 * Minimal error boundary. Its primary job here is to contain failures from
 * lazy-loaded chunks (e.g. a modal whose chunk fails to load after a stale
 * deploy) so they degrade the affected widget instead of unmounting the whole
 * React tree into a white screen.
 */
class ErrorBoundary extends Component<
  IErrorBoundaryProps,
  IErrorBoundaryState
> {
  state: IErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): IErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
